import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Star, Zap, Crown, Home, LogOut, User } from 'lucide-react';
import AppFooter from '@/components/AppFooter';
import SubscriptionCheckout from '@/components/SubscriptionCheckout';

const SubscriptionPlans = () => {
  const { logout } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const navigate = useNavigate();

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      icon: Check,
      color: 'bg-blue-500',
      price: 39.99,
      mealsPerWeek: 5,
      description: 'Perfect for getting started',
      features: [
        '5 meals per week',
        'AI-powered meal planning',
        'Basic nutrition tracking',
        '1 meal swap per week',
        'Standard delivery',
        'Email support'
      ],
      valuePerMeal: 8.00
    },
    {
      id: 'standard',
      name: 'Standard',
      icon: Star,
      color: 'bg-indigo-600',
      price: 69.99,
      mealsPerWeek: 10,
      description: 'Most popular - Best value',
      recommended: true,
      features: [
        '10 meals per week (Lunch + Dinner)',
        'AI-powered meal planning',
        'Advanced nutrition tracking',
        'Unlimited meal swaps',
        'Priority delivery',
        'Access to all vendors',
        'Premium support',
        'Sustainability badges'
      ],
      valuePerMeal: 7.00,
      savings: '£8/week vs Basic'
    },
    {
      id: 'premium',
      name: 'Premium',
      icon: Crown,
      color: 'bg-purple-600',
      price: 94.99,
      mealsPerWeek: 14,
      description: 'Complete meal coverage',
      features: [
        '14 meals per week (3 meals/day)',
        'AI-powered meal planning',
        'Advanced nutrition + wellness insights',
        'Unlimited meal swaps',
        'Express delivery (same-day options)',
        'Exclusive vendor access',
        'Priority support + nutrition coach',
        'Free sustainability upgrades',
        'Monthly wellness reports'
      ],
      valuePerMeal: 6.79,
      savings: '£15/week vs Basic'
    }
  ];

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  const handleCheckoutSuccess = () => {
    setShowCheckout(false);
    navigate('/subscriptions');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b sticky top-0 md:top-16 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="text-xl font-bold text-indigo-600">HealthLoop</Link>
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm"><Home className="w-4 h-4 mr-2" />Dashboard</Button>
              </Link>
              <Link to="/nutrition">
                <Button variant="ghost" size="sm">Browse Meals</Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="sm"><User className="w-4 h-4 mr-2" />Profile</Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-indigo-100 text-indigo-700 text-sm">
            ✨ Unlock Full Access
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Meal Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get AI-powered meal recommendations, unlimited swaps, and delivery from local healthy food vendors
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => {
            const IconComponent = plan.icon;
            
            return (
              <Card 
                key={plan.id}
                className={`relative ${
                  plan.recommended 
                    ? 'border-4 border-indigo-500 shadow-2xl scale-105' 
                    : 'border-2 border-gray-200 hover:border-indigo-300'
                } transition-all duration-300 hover:shadow-xl`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-indigo-600 text-white px-4 py-1 text-sm">
                      ⭐ RECOMMENDED
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-4">
                  <div className={`${plan.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                  <CardDescription className="text-base">{plan.description}</CardDescription>
                  
                  <div className="mt-6">
                    <div className="text-5xl font-bold text-gray-900">
                      £{plan.price}
                    </div>
                    <div className="text-gray-600 mt-2">per week</div>
                    <div className="text-sm text-indigo-600 mt-1">
                      £{plan.valuePerMeal.toFixed(2)}/meal
                    </div>
                    {plan.savings && (
                      <Badge variant="secondary" className="mt-2 bg-green-100 text-green-700">
                        Save {plan.savings}
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                <CardContent>
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    className={`w-full h-12 text-lg font-semibold mb-6 ${
                      plan.recommended
                        ? 'bg-indigo-600 hover:bg-indigo-700'
                        : 'bg-gray-800 hover:bg-gray-900'
                    }`}
                  >
                    Select {plan.name}
                  </Button>

                  <div className="space-y-3">
                    <p className="font-semibold text-gray-900 text-sm mb-3">What's Included:</p>
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature Comparison */}
        <Card className="bg-white/80 backdrop-blur">
          <CardHeader>
            <CardTitle>All Plans Include:</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Zap className="w-6 h-6 text-indigo-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">AI Meal Planning</h3>
                  <p className="text-sm text-gray-600">
                    Personalized weekly plans based on your health goals
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Flexible Swaps</h3>
                  <p className="text-sm text-gray-600">
                    Change meals up to 3 days before delivery
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Star className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Local Vendors</h3>
                  <p className="text-sm text-gray-600">
                    Fresh meals from certified healthy food providers
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <div className="mt-12 text-center">
          <p className="text-gray-600">
            Questions? <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-700 font-semibold">Contact Support</Link>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Cancel anytime • Pause up to 4 weeks • Flexible delivery
          </p>
        </div>
      </div>

      {/* Checkout Modal */}
      {showCheckout && selectedPlan && (
        <SubscriptionCheckout
          isOpen={showCheckout}
          onClose={() => setShowCheckout(false)}
          selectedPlan={selectedPlan}
          onSuccess={handleCheckoutSuccess}
        />
      )}

      <AppFooter />
    </div>
  );
};

export default SubscriptionPlans;
